import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Target, Flame, Trophy, Users, Calendar, LogIn } from "lucide-react";
import { motion } from "framer-motion";
import AvatarCard from "@/components/AvatarCard";
import SectionHeader from "@/components/SectionHeader";
import AssessmentRequestModal from "@/components/AssessmentRequestModal";
import LoginDialog from "@/components/LoginDialog";
import { PROGRAMS } from "@/lib/programs";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/a932b7ba-74cb-4b92-8e8d-7bb43aa3409e/images/a0be32aec4435252ef6cfd6e94f6bada167cade999bb8195707328e1cae79f0b.png";
const ABOUT_IMG =
    "https://static.prod-images.emergentagent.com/jobs/a932b7ba-74cb-4b92-8e8d-7bb43aa3409e/images/7c277ef22bc2f53a0beff5d9f146b2417aac07d5fcc36b21460d428b3cda1af4.png";

const PILLARS = [
    { icon: Target, title: "Speed", text: "Linear, lateral, and reactive — coached with precision and measured every block." },
    { icon: Flame, title: "Strength", text: "Progressive, sport-specific strength built on movement quality, not ego." },
    { icon: Trophy, title: "Performance", text: "Power output, conditioning, and recovery — programmed to peak on game day." },
    { icon: Users, title: "Culture", text: "A training floor that demands your best and celebrates the work it takes." },
];

const BAND_WORDS = [
    "SPEED",
    "STRENGTH",
    "POWER",
    "AGILITY",
    "HEALTH",
    "PERFORMANCE",
    "RECOVERY",
    "MINDSET",
];

export default function Home() {
    const [assessOpen, setAssessOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.get("book") === "1") {
            setAssessOpen(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[88svh] md:min-h-[92svh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Maximum Sports Conditioning training facility"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/65" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_25%_85%,rgba(31,45,85,0.75)_0%,transparent_70%)] mix-blend-screen" />
                    <div className="absolute inset-0 bg-[radial-gradient(35%_30%_at_85%_20%,rgba(230,0,0,0.18)_0%,transparent_75%)] mix-blend-screen" />
                    <div className="absolute inset-0 bg-grid-faint opacity-40" />
                </div>

                <div className="relative z-10 w-full pt-24 pb-32 md:pb-40">
                    <div className="max-w-7xl mx-auto px-6 md:px-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                            className="max-w-4xl"
                        >
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                Sports Performance · Est. for athletes
                            </div>
                            <h1
                                data-testid="hero-title"
                                className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.9] tracking-wider text-white"
                            >
                                LEAD WITH SPEED,
                                <br />
                                <span className="text-brand-red">PERFORM WITH POWER.</span>
                            </h1>
                            <p className="mt-6 text-zinc-300 text-base md:text-lg max-w-xl leading-relaxed">
                                Your athlete works hard — but the numbers say it's not translating yet.
                                Most athletes are more athletic than their numbers show; they just need
                                the right program. We measure first, then train what's actually holding
                                them back. Ages 8 and up, every stage, because everyone is an athlete for life.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-4">
                                <button
                                    type="button"
                                    data-testid="hero-cta-assessment"
                                    onClick={() => setAssessOpen(true)}
                                    className="btn-tactical"
                                >
                                    <Calendar className="w-4 h-4" />
                                    Have questions? Book a consult!
                                </button>
                                <Link to="/programs/school" data-testid="hero-cta-programs" className="btn-ghost-tactical">
                                    Explore Programs
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                                {!user && (
                                    <button
                                        type="button"
                                        data-testid="hero-cta-signin"
                                        onClick={() => setLoginOpen(true)}
                                        className="btn-ghost-tactical"
                                    >
                                        <LogIn className="w-4 h-4" />
                                        Member Sign In
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Stats strip */}
                <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-zinc-900 bg-black/70 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-900">
                        {[
                            { k: "3", v: "Athlete paths" },
                            { k: "8-60+", v: "Ages we coach" },
                            { k: "100%", v: "Coach-led" },
                            { k: "1", v: "Standard. Yours." },
                        ].map((s, i) => (
                            <div key={i} className="py-5 px-4 first:pl-0">
                                <div className="font-display text-3xl md:text-4xl tracking-wider text-white">
                                    {s.k}
                                </div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BAND — static, evenly distributed */}
            <section className="bg-gradient-to-r from-brand-navy-700 via-brand-navy-800 to-brand-red text-white border-y border-brand-navy-900">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 md:gap-x-10">
                        {BAND_WORDS.map((w, i) => (
                            <span key={w} className="inline-flex items-center gap-3 md:gap-5">
                                <span className="font-display text-xl md:text-2xl tracking-[0.22em] whitespace-nowrap">
                                    {w}
                                </span>
                                {i !== BAND_WORDS.length - 1 && (
                                    <span className="hidden md:inline-block w-1.5 h-1.5 bg-white/80 rotate-45" />
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* AVATARS / PROGRAMS */}
            <section id="avatars" className="py-24 md:py-32 bg-brand-obsidian">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
                        <div className="md:col-span-7">
                            <SectionHeader
                                eyebrow="Three tracks. One standard."
                                title="WHO ARE YOU TRAINING AS?"
                                kicker="Every athlete walks in with different goals. We built three distinct tracks so the work matches the moment — from foundational youth movement to elite-level prep to lifelong performance."
                            />
                        </div>
                        <div className="md:col-span-5 md:text-right">
                            <Link
                                to="/programs/school"
                                data-testid="avatars-cta-all"
                                className="btn-ghost-tactical inline-flex"
                            >
                                See all programs <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 md:gap-6">
                        {PROGRAMS.map((p, i) => (
                            <AvatarCard key={p.slug} program={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="relative py-24 md:py-32 bg-gradient-to-b from-brand-navy-950/60 via-black to-black border-y border-brand-navy-900/60 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_0%,rgba(31,45,85,0.4)_0%,transparent_75%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeader
                        eyebrow="The work"
                        title="WHAT WE COACH."
                        kicker="Four pillars. Every program. Every athlete. We don't drift, we don't trend-chase. We coach what makes athletes better."
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
                        {PILLARS.map((p, i) => {
                            // Alternate red / navy accents across the pillars
                            const useNavy = i % 2 === 1;
                            return (
                                <motion.div
                                    key={p.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.07 }}
                                    className={`group relative p-7 border border-zinc-900 bg-zinc-950/70 backdrop-blur-sm transition-colors duration-300 ${
                                        useNavy ? "hover:border-brand-navy-500" : "hover:border-brand-red"
                                    }`}
                                    data-testid={`pillar-${p.title.toLowerCase()}`}
                                >
                                    <div className={`inline-flex items-center justify-center w-11 h-11 bg-black border border-zinc-800 mb-5 transition-all duration-300 ${
                                        useNavy
                                            ? "text-brand-navy-300 group-hover:bg-brand-navy-700 group-hover:border-brand-navy-700"
                                            : "text-brand-red group-hover:bg-brand-red group-hover:border-brand-red"
                                    } group-hover:text-white`}>
                                        <p.icon className="w-5 h-5" />
                                    </div>
                                    <div className="font-display text-3xl tracking-wider text-white leading-none">
                                        {p.title}
                                    </div>
                                    <p className="mt-3 text-sm text-zinc-200 leading-relaxed">{p.text}</p>
                                    <div className={`mt-6 text-[10px] tracking-[0.25em] uppercase ${
                                        useNavy ? "text-brand-navy-300" : "text-zinc-200"
                                    }`}>
                                        0{i + 1} / 04
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ABOUT / PHILOSOPHY */}
            <section className="relative py-24 md:py-32 bg-brand-obsidian overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
                    <div className="md:col-span-6 relative">
                        <div className="relative aspect-[4/5] overflow-hidden border border-zinc-900">
                            <img
                                src={ABOUT_IMG}
                                alt="Athlete training"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
                            <div className="absolute -bottom-3 -right-3 w-32 h-32 stripe-accent opacity-90" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <SectionHeader
                            eyebrow="The philosophy"
                            title={
                                <>
                                    THIS ISN'T A GYM.
                                    <br />
                                    <span className="text-brand-red">IT'S A STANDARD.</span>
                                </>
                            }
                        />
                        <div className="mt-7 space-y-5 text-zinc-300 text-base md:text-lg leading-relaxed">
                            <p>
                                We believe athletic development should be earned, not bought. Every block of programming
                                is built around a single question — <span className="text-white font-semibold">what does this athlete need to get better, today?</span>
                            </p>
                            <p>
                                Coaches lead every session. Reps are watched. Intent is non-negotiable. The standard is the
                                same whether you're 7 or 47 — show up, do the work, leave better than you came.
                            </p>
                        </div>

                        <div className="mt-9 grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
                            {[
                                { k: "Coach-led", v: "Every rep" },
                                { k: "Measured", v: "Every block" },
                                { k: "Tailored", v: "Every athlete" },
                            ].map((s, i) => (
                                <div key={i} className="bg-brand-obsidian p-5">
                                    <div className="font-display text-xl text-brand-red tracking-wider">
                                        {s.k}
                                    </div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative bg-gradient-to-br from-brand-red via-brand-red to-brand-navy-800 text-white overflow-hidden">
                <div className="absolute inset-0 bg-grid-faint opacity-20 pointer-events-none" />
                <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_85%_50%,rgba(31,45,85,0.65)_0%,transparent_70%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <div className="text-[11px] tracking-[0.3em] uppercase mb-4 opacity-80">
                            Ready to start?
                        </div>
                        <h2 className="font-display text-5xl md:text-7xl tracking-wider leading-[0.9]">
                            STOP WAITING.
                            <br />
                            START TRAINING.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right space-y-2">
                        <button
                            type="button"
                            data-testid="bottom-cta-assessment"
                            onClick={() => setAssessOpen(true)}
                            className="inline-flex items-center gap-3 bg-black hover:bg-zinc-900 text-white font-bold uppercase tracking-[0.15em] px-7 py-4 text-sm border border-black transition-colors"
                        >
                            Have questions? Book a consult! <ArrowRight className="w-4 h-4" />
                        </button>
                        <div>
                            <Link
                                to="/programs/school"
                                data-testid="bottom-cta-programs"
                                className="inline-flex items-center gap-3 text-white/90 hover:text-white font-bold uppercase tracking-[0.15em] px-1 text-xs"
                            >
                                or view programs <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            <AssessmentRequestModal open={assessOpen} onClose={() => setAssessOpen(false)} />
            <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
        </div>
    );
}
